import type {
  ClassDef,
  ContentRegistry,
  Controller,
  DungeonDef,
  KeystoneConfig,
  MPlusState,
  NpcDef,
  PackId,
  Radians,
  ResourceId,
  ResourcePool,
  ScalingMults,
  Stats,
  SwingState,
  Unit,
  UnitId,
  Vec2,
  World,
  WorldOps,
} from '../contracts';
import { AUTO_SHOT_RANGE, PLAYER_COMBAT_REACH } from '../contracts';
import { seedRngState } from './rng';

export const DEFAULT_STATS: Stats = {
  power: 1000,
  haste: 0,
  crit: 0.05,
  mastery: 0,
  versatility: 0,
  armor: 0,
  dodge: 0,
  parry: 0,
  block: 0,
  blockAmount: 0.3,
  missChance: 0,
};

export function initialMPlusState(dungeon: DungeonDef | undefined, keystone: KeystoneConfig): MPlusState {
  return {
    phase: 'pre',
    level: keystone.level,
    affixes: [...keystone.affixes],
    timerMs: dungeon?.timerMs ?? 0,
    startedAt: null,
    elapsedMs: 0,
    forces: 0,
    forcesRequired: dungeon?.forcesRequired ?? 0,
    deaths: 0,
    deathPenaltyMs: 0,
    bossesKilled: [],
    packsKilled: [],
    completedAt: null,
    chest: null,
    inCombat: false,
    combatSince: null,
    outOfCombatMs: 0,
    pulls: 0,
    wipes: 0,
  };
}

export function createEmptyWorld(seed: number, dungeonId: string, mplus: MPlusState): World {
  return {
    tick: 0,
    time: 0,
    seed,
    rngState: seedRngState(seed),
    dungeonId,
    units: {},
    unitOrder: [],
    partyIds: [],
    packs: {},
    telegraphs: [],
    zones: [],
    encounter: null,
    encountersDone: [],
    mplus,
    nextSeq: 1,
    events: [],
  };
}

export function nextId(world: World, prefix: string): string {
  const id = `${prefix}${world.nextSeq}`;
  world.nextSeq += 1;
  return id;
}

function baseUnit(id: UnitId, pos: Vec2, facing: Radians): Unit {
  return {
    id,
    name: id,
    kind: 'npc',
    faction: 'enemy',
    controller: 'ai',
    role: null,
    classId: null,
    npcId: null,
    tier: null,
    pos: { x: pos.x, y: pos.y },
    facing,
    radius: 0.5,
    combatReach: PLAYER_COMBAT_REACH,
    moveSpeed: 7,
    move: null,
    moving: false,
    hp: 1,
    maxHp: 1,
    alive: true,
    diedAt: null,
    resources: {},
    stats: { ...DEFAULT_STATS },
    scaling: { hp: 1, damage: 1 },
    auras: [],
    cooldowns: {},
    gcdUntil: 0,
    cast: null,
    queued: null,
    swing: null,
    meleeDamage: null,
    targetId: null,
    lockouts: {},
    dr: {},
    threat: null,
    ai: null,
    packId: null,
    encounterId: null,
    home: { x: pos.x, y: pos.y },
    homeFacing: facing,
    inCombat: false,
    recentDamage: [],
    ccImmune: false,
    tauntDr: false,
    summonerId: null,
    color: '#888888',
  };
}

export function makePlayerUnit(
  def: ClassDef,
  spec: { id: UnitId; name: string; controller: Controller; pos: Vec2; facing: Radians },
): Unit {
  const u = baseUnit(spec.id, spec.pos, spec.facing);
  const resources: Partial<Record<ResourceId, ResourcePool>> = {};
  for (const r of def.resources) resources[r.id] = { cur: r.start, max: r.max };
  return {
    ...u,
    name: spec.name,
    kind: 'player',
    faction: 'party',
    controller: spec.controller,
    role: def.role,
    classId: def.id,
    ...(def.specId ? { specId: def.specId } : {}), // +specs
    radius: 0.5,
    combatReach: PLAYER_COMBAT_REACH,
    moveSpeed: def.moveSpeed,
    hp: def.maxHp,
    maxHp: def.maxHp,
    resources,
    stats: { ...def.baseStats },
    swing: playerSwing(def), // +autoattack
    meleeDamage: def.melee?.damage ?? def.weapon?.damage ?? null,
    color: def.color,
  };
}

/** +autoattack: a hero's swing timer from its class weapon (melee / dual wield / bow / staff). */
function playerSwing(def: ClassDef): SwingState | null {
  if (def.melee) {
    const oh = def.melee.offhand;
    return { speedMs: def.melee.speedMs, nextAt: 0, kind: 'melee', ...(oh ? { oh: { speedMs: oh.speedMs, nextAt: 0, mult: oh.mult } } : {}) };
  }
  const wpn = def.weapon;
  if (!wpn) return null;
  return { speedMs: wpn.speedMs, nextAt: 0, kind: wpn.kind, ...(wpn.kind === 'ranged' ? { range: wpn.range ?? AUTO_SHOT_RANGE } : {}) };
}

export function makeNpcUnit(
  def: NpcDef,
  baseHp: number,
  scaling: ScalingMults,
  spec: { id: UnitId; pos: Vec2; facing: Radians; packId: PackId | null; summonerId?: UnitId },
): Unit {
  const u = baseUnit(spec.id, spec.pos, spec.facing);
  const maxHp = Math.max(1, Math.round(baseHp * scaling.hp));
  const isBoss = def.tier === 'boss';
  return {
    ...u,
    name: def.name,
    npcId: def.id,
    tier: def.tier,
    radius: def.radius,
    combatReach: def.combatReach,
    moveSpeed: def.moveSpeed,
    hp: maxHp,
    maxHp,
    stats: { ...DEFAULT_STATS, crit: 0, ...def.baseStats },
    scaling: { ...scaling },
    swing: def.melee ? { speedMs: def.melee.speedMs, nextAt: 0 } : null,
    meleeDamage: def.melee ? { base: def.melee.dmg } : null,
    threat: {
      entries: {},
      victimId: null,
      nextReselectAt: 0,
      taunt: null,
      fixate: null,
      tauntDrCount: 0,
      tauntDrResetAt: 0,
    },
    ai: { mode: 'idle', timers: {}, phaseIndex: 0, engagedAt: null, nextEvadeCheckAt: 0, patrolIndex: 0 },
    packId: spec.packId,
    ccImmune: def.ccImmune ?? isBoss,
    tauntDr: def.tauntDr ?? isBoss,
    summonerId: spec.summonerId ?? null,
    color: def.color,
  };
}

/** Insert a unit into the world (keeps unitOrder deterministic). */
export function addUnit(world: World, u: Unit): Unit {
  if (world.units[u.id]) throw new Error(`duplicate unit id ${u.id}`);
  world.units[u.id] = u;
  world.unitOrder.push(u.id);
  return u;
}

/**
 * WorldOps bound to a world getter (the world object can be swapped by restore()).
 * `scaling` is injected so core does not depend on the mplus implementation.
 */
export function createWorldOps(
  getWorld: () => World,
  content: ContentRegistry,
  scaling: (npcId: string) => ScalingMults,
  /** +field: level stamped on new NPCs (DungeonDef.mobLevel); field spawns set their own. */
  npcLevel?: () => number | undefined,
): WorldOps {
  return {
    spawnPlayer(spec) {
      const w = getWorld();
      const def = content.class(spec.classId);
      if (!def) throw new Error(`unknown class ${spec.classId}`);
      const id = spec.id ?? nextId(w, 'p');
      return addUnit(w, makePlayerUnit(def, { ...spec, id }));
    },
    spawnNpc(spec) {
      const w = getWorld();
      const def = content.npc(spec.npcId);
      if (!def) throw new Error(`unknown npc ${spec.npcId}`);
      const id = nextId(w, 'n');
      // +field: a field mob's summons share its level and scaling (fields have no keystone scaling).
      const summoner = spec.summonerId ? w.units[spec.summonerId] : undefined;
      const inherit = !!w.field && summoner?.level !== undefined;
      const u = makeNpcUnit(def, content.npcBaseHp(def.id), inherit ? summoner!.scaling : scaling(def.id), { ...spec, id });
      const level = inherit ? summoner!.level : npcLevel?.();
      if (level !== undefined) u.level = level;
      addUnit(w, u);
      if (spec.packId) {
        const pack = w.packs[spec.packId];
        if (pack) pack.memberIds.push(id);
      }
      return u;
    },
    despawn(unitId) {
      const w = getWorld();
      delete w.units[unitId];
      w.unitOrder = w.unitOrder.filter((id) => id !== unitId);
      for (const p of Object.values(w.packs)) p.memberIds = p.memberIds.filter((id) => id !== unitId);
    },
    unit(id) {
      return getWorld().units[id];
    },
    units(filter) {
      const w = getWorld();
      const out: Unit[] = [];
      for (const id of w.unitOrder) {
        const u = w.units[id];
        if (u && u.alive && (!filter || filter(u))) out.push(u);
      }
      return out;
    },
  };
}
